/*
 * Copyright 2026 Sony Corporation
 */
import { z } from 'zod';

/**
 * Model usage per user and calendar month (UTC), and the limit that stops the assistant (cost-optimization.md C0).
 * The limit is in tokens: every token a model reads or writes (input, output, cache read, cache write) counts as one.
 * What that costs is worked out from the token kinds and the model's price.
 */
export const TOKEN_KINDS = ['input', 'output', 'cache_read', 'cache_write'] as const;
export type TokenKind = (typeof TOKEN_KINDS)[number];

/** Tokens per model id and kind, as the models report them. */
export type ModelTokens = Record<string, Partial<Record<TokenKind, number>>>;

/** US dollars per million tokens, at the Bedrock list price of the global inference profiles (eu-west-1). */
export interface TokenPrice {
  input: number;
  output: number;
  cache_read: number;
  cache_write: number;
}

const PRICES: { match: RegExp; price: TokenPrice }[] = [
  { match: /claude-haiku-5-5/, price: { input: 0.1, output: 0.5, cache_read: 0.01, cache_write: 0.125 } },
  { match: /claude-sonnet-5-5/, price: { input: 2, output: 10, cache_read: 0.1, cache_write: 2.5 } },
  { match: /claude-sonnet-5(?!-)/, price: { input: 2, output: 10, cache_read: 0.2, cache_write: 2.5 } },
  { match: /claude-opus-5-5/, price: { input: 4, output: 20, cache_read: 0.2, cache_write: 5 } },
];

/** A geographic (EU, US, ...) inference profile costs 10% more than the global one. */
const REGIONAL_SURCHARGE = 1.1;

/** The price of a model id; an unknown model is priced as Sonnet 5.5, so a cost is never understated. */
export const priceOf = (modelId: string): TokenPrice => {
  const base = (PRICES.find((p) => p.match.test(modelId)) ?? PRICES[1]).price;
  const factor = /^(eu|us|apac|jp|au)\./.test(modelId) ? REGIONAL_SURCHARGE : 1;
  return {
    input: base.input * factor,
    output: base.output * factor,
    cache_read: base.cache_read * factor,
    cache_write: base.cache_write * factor,
  };
};

export const totalTokens = (tokens: ModelTokens): number =>
  Object.values(tokens).reduce((sum, kinds) => sum + TOKEN_KINDS.reduce((s, kind) => s + (kinds[kind] ?? 0), 0), 0);

export const tokensCost = (tokens: ModelTokens): number =>
  Object.entries(tokens).reduce((sum, [model, kinds]) => {
    const price = priceOf(model);
    return sum + TOKEN_KINDS.reduce((s, kind) => s + ((kinds[kind] ?? 0) * price[kind]) / 1_000_000, 0);
  }, 0);

/** Tokens by kind over all models. */
export const tokensByKind = (tokens: ModelTokens): Record<TokenKind, number> => {
  const out = { input: 0, output: 0, cache_read: 0, cache_write: 0 };
  for (const kinds of Object.values(tokens)) {
    for (const kind of TOKEN_KINDS) {
      out[kind] += kinds[kind] ?? 0;
    }
  }
  return out;
};

/** The month usage is counted in, "YYYY-MM" (UTC). */
export const usagePeriod = (at: Date = new Date()): string => at.toISOString().slice(0, 7);

export const UsagePeriodSchema = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/);

/** When a month's count starts over: the first instant of the next month (UTC). */
export const periodResetsAt = (period: string): string => {
  const [year, month] = period.split('-').map(Number);
  return new Date(Date.UTC(year, month, 1)).toISOString();
};

/** The monthly limit of a user no administrator has set one for. */
export const DEFAULT_TOKEN_LIMIT = 100_000_000;

/** Above this share of the limit the user is warned. */
export const USAGE_WARNING_PERCENT = 80;

/**
 * Rough rates to turn tokens into dollars and apps, until this month has enough of its own: about $0.9 per million
 * tokens and 4 million tokens per app (a roadmap of three or four steps), measured on toi in November 2026.
 */
export const GUIDELINE_DEFAULTS = { usd_per_mtoken: 0.9, tokens_per_app: 4_000_000 } as const;

/** Apps a month must have made before its own rates replace the defaults. */
export const GUIDELINE_MIN_APPS = 5;

export const GuidelineSchema = z.object({
  usd_per_mtoken: z.number().nonnegative(),
  tokens_per_app: z.number().positive(),
  /** "measured": this month's usage of all users; "default": GUIDELINE_DEFAULTS. */
  basis: z.enum(['measured', 'default']),
});
export type Guideline = z.infer<typeof GuidelineSchema>;

/** The rates of a month's usage across users, or the defaults while it has too little to go on. */
export const guidelineOf = (users: { tokens: number; cost_usd: number; apps: number }[]): Guideline => {
  const tokens = users.reduce((s, u) => s + u.tokens, 0);
  const cost = users.reduce((s, u) => s + u.cost_usd, 0);
  const apps = users.reduce((s, u) => s + u.apps, 0);
  if (apps < GUIDELINE_MIN_APPS || tokens <= 0) {
    return { ...GUIDELINE_DEFAULTS, basis: 'default' };
  }
  return { usd_per_mtoken: (cost / tokens) * 1_000_000, tokens_per_app: tokens / apps, basis: 'measured' };
};

/** The signed-in user's own usage this month. */
export const UsageSchema = z.object({
  period: UsagePeriodSchema,
  tokens: z.number().int().nonnegative(),
  cost_usd: z.number().nonnegative(),
  limit_tokens: z.number().int().nonnegative(),
  /** Share of the limit used, 0-100 and more once it is passed. */
  percent: z.number().nonnegative(),
  /** The limit is reached: the assistant takes no new work until the month ends or the limit is raised. */
  blocked: z.boolean(),
  resets_at: z.string().datetime(),
});
export type Usage = z.infer<typeof UsageSchema>;

export const AdminUserUsageSchema = z.object({
  user_id: z.string(),
  /** Null for a user who is no longer in the user pool. */
  email: z.string().nullable(),
  admin: z.boolean(),
  tokens: z.number().int().nonnegative(),
  tokens_by_kind: z.object({
    input: z.number().int().nonnegative(),
    output: z.number().int().nonnegative(),
    cache_read: z.number().int().nonnegative(),
    cache_write: z.number().int().nonnegative(),
  }),
  cost_usd: z.number().nonnegative(),
  /** Projects the assistant worked on this month. */
  apps: z.number().int().nonnegative(),
  limit_tokens: z.number().int().nonnegative(),
  /** An administrator set the limit; otherwise it is the default. */
  limit_custom: z.boolean(),
  percent: z.number().nonnegative(),
  blocked: z.boolean(),
  updated_at: z.string().nullable(),
});
export type AdminUserUsage = z.infer<typeof AdminUserUsageSchema>;

export const ListUsageQuerySchema = z.object({ period: UsagePeriodSchema.optional() });

export const ListUsageResponseSchema = z.object({
  period: UsagePeriodSchema,
  users: z.array(AdminUserUsageSchema),
  default_limit_tokens: z.number().int().nonnegative(),
  guideline: GuidelineSchema,
});
export type ListUsageResponse = z.infer<typeof ListUsageResponseSchema>;

/** A user's monthly limit in tokens; null goes back to the default. */
export const UpdateUsageLimitRequestSchema = z.object({
  limit_tokens: z.number().int().nonnegative().max(100_000_000_000).nullable(),
});
export type UpdateUsageLimitRequest = z.infer<typeof UpdateUsageLimitRequestSchema>;

export const UpdateUsageLimitResponseSchema = z.object({
  user_id: z.string(),
  limit_tokens: z.number().int().nonnegative(),
  limit_custom: z.boolean(),
});
export type UpdateUsageLimitResponse = z.infer<typeof UpdateUsageLimitResponseSchema>;

/** What a user is told when the assistant refuses work because of the limit. */
export const USAGE_LIMIT_MESSAGE =
  'You have used your token limit for this month, so the assistant has stopped. Ask an administrator to raise the limit, or wait for next month.';
